import {FC, useEffect, useMemo, useState} from 'react'
import axios from 'axios'
import {Modal} from 'react-bootstrap'
import {KTSVG} from '../../../_metronic/helpers'
import {useAuth} from '../../modules/auth'

type WorkloadRow = {
  writerId: number
  fullName: string
  userName: string
  totalCount: number
  writtenCount: number
  unwrittenCount: number
  approvedCount: number
  returnedCount: number
  packageCount: number
  nearestDeadline: string
}

type WorkloadResponse = {
  generatedAt: string
  rows: WorkloadRow[]
}

type PackageDetail = {
  packageId: number
  packageName: string
  totalCount: number
  writtenCount: number
  unwrittenCount: number
  approvedCount: number
  returnedCount: number
}

type RequestDetail = {
  requestId: number
  requestName: string
  lastDate: string
  totalCount: number
  writtenCount: number
  unwrittenCount: number
  approvedCount: number
  returnedCount: number
  packages: PackageDetail[]
}

type WorkloadDetailResponse = {
  writerId: number
  fullName: string
  userName: string
  generatedAt: string
  requests: RequestDetail[]
}

type StateFilter = 'all' | 'written' | 'unwritten'
type MetricTone = 'neutral' | 'info' | 'danger' | 'success' | 'warning'

const metricStyles: Record<MetricTone, {color: string; backgroundColor: string; border: string}> = {
  neutral: {color: '#4b5675', backgroundColor: '#f1f1f2', border: '1px solid #d8dbe2'},
  info: {color: '#055160', backgroundColor: '#cff4fc', border: '1px solid #9eeaf9'},
  danger: {color: '#842029', backgroundColor: '#f8d7da', border: '1px solid #f1aeb5'},
  success: {color: '#0f5132', backgroundColor: '#d1e7dd', border: '1px solid #a3cfbb'},
  warning: {color: '#664d03', backgroundColor: '#fff3cd', border: '1px solid #ffe69c'},
}

const formatDate = (value?: string) => {
  if (!value) return '-'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString('tr-TR')
}

const CountMetric: FC<{label: string; value: number; tone?: MetricTone}> = ({label, value, tone = 'neutral'}) => (
  <span
    className='d-inline-flex align-items-center gap-2 rounded-2 px-2 py-1 fs-8 fw-bold text-nowrap'
    style={metricStyles[tone]}
  >
    <span>{label}</span>
    <strong className='fs-7' style={{fontVariantNumeric: 'tabular-nums'}}>{value}</strong>
  </span>
)

const QuestionMetrics: FC<{
  total: number
  written: number
  unwritten: number
  approved: number
  returned: number
}> = ({total, written, unwritten, approved, returned}) => (
  <div className='d-flex flex-wrap gap-2'>
    <CountMetric label='Toplam' value={total} />
    <CountMetric label='Yazılmış' value={written} tone='info' />
    <CountMetric label='Yazılmamış' value={unwritten} tone={unwritten > 0 ? 'danger' : 'success'} />
    <CountMetric label='Onaylı' value={approved} tone='success' />
    <CountMetric label='İade' value={returned} tone={returned > 0 ? 'warning' : 'neutral'} />
  </div>
)

const AdminQuestionWorkload: FC = () => {
  const {currentUser} = useAuth()
  const roleName = currentUser?.roleName ?? currentUser?.RoleName ?? ''
  const isAdmin = roleName.trim().toUpperCase() === 'ADMIN'
  const [rows, setRows] = useState<WorkloadRow[]>([])
  const [writerId, setWriterId] = useState('')
  const [stateFilter, setStateFilter] = useState<StateFilter>('all')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [selectedWriter, setSelectedWriter] = useState<WorkloadRow | null>(null)
  const [details, setDetails] = useState<WorkloadDetailResponse | null>(null)
  const [detailsLoading, setDetailsLoading] = useState(false)
  const [detailsError, setDetailsError] = useState('')

  useEffect(() => {
    if (!isAdmin) return

    setLoading(true)
    axios.get<WorkloadResponse>(`${process.env.REACT_APP_API_URL}/AdminQuestionWorkload`)
      .then((response) => setRows(response.data.rows || []))
      .catch(() => setError(true))
      .finally(() => setLoading(false))
  }, [isAdmin])

  const visibleRows = useMemo(() => rows.filter((row) => {
    if (writerId && row.writerId !== Number(writerId)) return false
    if (stateFilter === 'written' && row.writtenCount === 0) return false
    if (stateFilter === 'unwritten' && row.unwrittenCount === 0) return false
    return true
  }), [rows, stateFilter, writerId])

  const openDetails = async (row: WorkloadRow) => {
    setSelectedWriter(row)
    setDetails(null)
    setDetailsError('')
    setDetailsLoading(true)

    try {
      const response = await axios.get<WorkloadDetailResponse>(
        `${process.env.REACT_APP_API_URL}/AdminQuestionWorkload/${row.writerId}/details`
      )
      setDetails(response.data)
    } catch (requestError: any) {
      setDetailsError(
        requestError?.response?.data?.message || 'Talep ve paket ayrıntıları şu anda alınamadı.'
      )
    } finally {
      setDetailsLoading(false)
    }
  }

  const closeDetails = () => {
    setSelectedWriter(null)
    setDetails(null)
    setDetailsError('')
  }

  if (!isAdmin) return null

  return (
    <>
      <div className='card mb-8'>
        <div className='card-header border-0 pt-5'>
          <div>
            <h2 className='card-title fw-bolder text-gray-900 mb-1'>Kişi Bazlı Soru Takibi</h2>
            <div className='text-gray-700 fs-6'>Aktif taleplerde kişilerin yazılan ve yazılmayan soruları.</div>
          </div>
        </div>
        <div className='card-body pt-4'>
          <div className='row g-3 mb-5'>
            <div className='col-12 col-md-6'>
              <label className='form-label fw-semibold text-gray-900'>Kişi</label>
              <select className='form-select' value={writerId} onChange={(event) => setWriterId(event.target.value)}>
                <option value=''>Tüm kişiler</option>
                {rows.map((row) => <option value={row.writerId} key={row.writerId}>{row.fullName || row.userName}</option>)}
              </select>
            </div>
            <div className='col-12 col-md-6'>
              <label className='form-label fw-semibold text-gray-900'>Durum</label>
              <select className='form-select' value={stateFilter} onChange={(event) => setStateFilter(event.target.value as StateFilter)}>
                <option value='all'>Tüm durumlar</option>
                <option value='written'>Yazılan sorusu olanlar</option>
                <option value='unwritten'>Yazılmayan sorusu olanlar</option>
              </select>
            </div>
          </div>

          {loading && <div className='text-muted'>Soru durumu hazırlanıyor...</div>}
          {error && <div className='alert alert-warning'>Kişi bazlı soru durumu şu anda alınamadı.</div>}
          {!loading && !error && (
            <div className='table-responsive'>
              <table className='table align-middle table-row-dashed dataTable no-footer gy-4'>
                <thead>
                  <tr className='text-gray-800 fw-bolder'>
                    <th>Kişi</th>
                    <th>Kapsam</th>
                    <th>Soru özeti</th>
                    <th>En yakın son tarih</th>
                    <th className='text-end'>Detay</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((row) => (
                    <tr key={row.writerId}>
                      <td>
                        <span className='fw-bolder text-gray-900'>{row.fullName || row.userName}</span>
                        <div className='text-gray-700 fs-7'>{row.userName}</div>
                      </td>
                      <td><CountMetric label='Paket' value={row.packageCount} /></td>
                      <td>
                        <QuestionMetrics
                          total={row.totalCount}
                          written={row.writtenCount}
                          unwritten={row.unwrittenCount}
                          approved={row.approvedCount}
                          returned={row.returnedCount}
                        />
                      </td>
                      <td className='text-gray-800 fw-semibold'>{formatDate(row.nearestDeadline)}</td>
                      <td className='text-end'>
                        <button
                          type='button'
                          className='btn btn-sm btn-light-primary d-inline-flex align-items-center gap-2 text-nowrap'
                          onClick={() => openDetails(row)}
                          aria-label={`${row.fullName || row.userName} talep ve paket detaylarını aç`}
                          title='Talep ve paket detaylarını aç'
                        >
                          <KTSVG path='/media/icons/duotune/general/gen005.svg' className='svg-icon-3' />
                          <span>Detay</span>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {visibleRows.length === 0 && <div className='text-center text-muted py-8'>Seçilen ölçütlere uygun kayıt yok.</div>}
            </div>
          )}
        </div>
      </div>

      <Modal show={Boolean(selectedWriter)} onHide={closeDetails} size='xl' centered scrollable>
        <Modal.Header closeButton>
          <Modal.Title>
            <span className='d-block fw-bolder text-gray-900'>{selectedWriter?.fullName || selectedWriter?.userName}</span>
            <span className='d-block text-gray-600 fs-7 fw-semibold mt-1'>Talep ve paket bazlı soru dağılımı</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className='bg-light'>
          {detailsLoading && (
            <div className='d-flex align-items-center justify-content-center gap-3 py-10 text-gray-700'>
              <span className='spinner-border spinner-border-sm' aria-hidden='true' />
              Ayrıntılar hazırlanıyor...
            </div>
          )}
          {detailsError && <div className='alert alert-danger mb-0'>{detailsError}</div>}
          {!detailsLoading && !detailsError && details?.requests.map((request) => (
            <section key={request.requestId} className='card border mb-5 shadow-sm'>
              <div className='card-header d-flex flex-wrap align-items-center justify-content-between gap-3 py-4'>
                <div>
                  <h3 className='fw-bolder text-gray-900 fs-5 mb-1'>{request.requestName || `Talep #${request.requestId}`}</h3>
                  <span className='text-gray-600 fs-7'>Yazar son tarihi: {formatDate(request.lastDate)}</span>
                </div>
                <div className='d-flex flex-wrap align-items-center justify-content-end gap-2'>
                  <QuestionMetrics
                    total={request.totalCount}
                    written={request.writtenCount}
                    unwritten={request.unwrittenCount}
                    approved={request.approvedCount}
                    returned={request.returnedCount}
                  />
                  <a className='btn btn-sm btn-light-primary text-nowrap' href={`/package/list?id=${request.requestId}`}>
                    Paketleri aç
                  </a>
                </div>
              </div>
              <div className='card-body py-2'>
                <div className='table-responsive'>
                  <table className='table align-middle table-row-dashed mb-0'>
                    <thead>
                      <tr className='text-gray-700 fw-bolder'>
                        <th>Paket</th>
                        <th>Soru özeti</th>
                      </tr>
                    </thead>
                    <tbody>
                      {request.packages.map((packageItem) => (
                        <tr key={packageItem.packageId}>
                          <td className='fw-bolder text-gray-900'>{packageItem.packageName || `Paket #${packageItem.packageId}`}</td>
                          <td>
                            <QuestionMetrics
                              total={packageItem.totalCount}
                              written={packageItem.writtenCount}
                              unwritten={packageItem.unwrittenCount}
                              approved={packageItem.approvedCount}
                              returned={packageItem.returnedCount}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>
          ))}
        </Modal.Body>
        <Modal.Footer>
          <button type='button' className='btn btn-light' onClick={closeDetails}>Kapat</button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default AdminQuestionWorkload
